import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// 🛰️ Unified types to resolve property mismatches
import { AppState, AppAction, KanbanTask, TaskStatus, BoardConfig, TemplateType, DataScope } from '@/types'; 

// --- Feature Imports ---
import { useTaskState } from '../features/kanban-engine/hooks/useTaskState';
import { groupTasksByContext } from '../features/kanban-engine/utils/grouping';

// --- Shared Components ---
import { Button } from '@/components/ui/Button';
import { 
  ShoppingCart, History, Utensils, Filter, 
  Sparkles, CheckCircle2, Pizza, ChevronRight, 
  Check, Settings, X, Shield, Users, Layers, Lock
} from 'lucide-react';

// --- Local Feature Bricks ---
import { InlineAddGrocery } from './InlineAddGrocery';
import { CategoryManager } from '../features/inventory/CategoryManager';
import { guessCategory } from '../features/inventory/utils/groceryCategories';

interface InventoryProps {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const GROCERY_BOARD: BoardConfig = {
  id: 'household-groceries-001',
  title: 'Groceries',
  templateType: TemplateType.GROCERY,
  allowedStatuses: ['todo', 'in_progress', 'pantry', 'history'],
  visibility: 'member',
  isCollapsed: false,
  defaultGroupBy: 'category',
  activeFilters: []
};

export const Inventory: React.FC<InventoryProps> = ({ state, dispatch }) => {
  const { t } = useTranslation();
  const currentUserId = state.currentUser?.id || 'guest';
  const currentHouseholdId = state.currentHouseholdId;
  
  // --- 🛡️ HYBRID SCOPE STATE ---
  const [activeScope, setActiveScope] = useState<DataScope>(
    currentHouseholdId ? 'BOTH' : 'PERSONAL'
  );

  // --- 🛰️ ENGINE INTEGRATION ---
  const inventoryData = (state.inventory as KanbanTask[]) || [];
  
  // 🛡️ Passing Scoping context to TaskState hook
  const { tasks, moveTask, createTask, consumeQuantity, isOnline } = useTaskState(
    inventoryData, 
    currentUserId,
    dispatch,
    currentHouseholdId,
    activeScope,
    state.currentUser?.permissions // Use currentUser.permissions here
  );

  const [activeTab, setActiveTab] = useState<TaskStatus>('todo');
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [isAdminOpen, setIsAdminOpen] = useState(false);

  // --- 🧠 PREDICTIVE SUGGESTIONS (Scope Aware) ---
  const suggestions = useMemo(() => {
    const todayMillis = Date.now();
    return tasks.filter((t: KanbanTask) => {
      // 🛡️ Filter suggestions based on privacy boundary
      if (activeScope === 'PERSONAL' && t.scope !== 'personal') return false;
      if (activeScope === 'SHARED' && t.scope !== 'household') return false;

      const consumption = t.consumption;
      if (t.templateType !== 'grocery' || !consumption?.lastAddedHistory || t.status !== 'history') return false;
      
      const isAlreadyActive = tasks.some((active: KanbanTask) => 
        active.title.toLowerCase() === t.title.toLowerCase() && active.status !== 'history'
      );
      if (isAlreadyActive) return false;

      const lastDate = new Date(consumption.lastAddedHistory);
      const cycle = consumption.averageCycle || 14; 
      const predictedDate = new Date(lastDate);
      predictedDate.setDate(lastDate.getDate() + cycle);
      
      const diffDays = Math.ceil((predictedDate.getTime() - todayMillis) / (1000 * 60 * 60 * 24));
      return diffDays <= 2;
    });
  }, [tasks, activeScope]);

  // --- 📂 DYNAMIC GROUPING & FILTERING ---
  const groupedData = useMemo(() => {
    // 🛡️ Filter by Active Scope before grouping
    const scopedTasks = tasks.filter((t: KanbanTask) => {
      const matchStatus = t.status === activeTab;
      let matchScope = true;
      if (activeScope === 'PERSONAL') matchScope = t.scope === 'personal';
      if (activeScope === 'SHARED') matchScope = t.scope === 'household';
      
      // 🔒 Privacy Guard: Never leak personal items of other users
      const privacyCheck = t.scope === 'personal' ? t.ownerId === currentUserId : true;

      return matchStatus && matchScope && privacyCheck;
    });

    return groupTasksByContext(scopedTasks, GROCERY_BOARD, selectedCategory, currentUserId, currentHouseholdId, activeScope);
  }, [tasks, activeTab, selectedCategory, activeScope, currentUserId]);

  const categories = useMemo(() => {
    const tags = tasks
      .filter((t: KanbanTask) => t.boardId === GROCERY_BOARD.id)
      .flatMap((t: KanbanTask) => t.tags || []);
    return ['ALL', ...Array.from(new Set(tags))];
  }, [tasks]);

  // --- HANDLERS ---
  const handleAddItem = (name: string, _quantity: number, _unit: string) => {
    const category = guessCategory(name);
    // 🛡️ Default creation scope based on current toggle (forced to personal if no household)
    const targetScope = activeScope === 'SHARED' ? 'household' : 'personal';
    createTask(name, TemplateType.GROCERY, GROCERY_BOARD.id, [category], targetScope);
  };

  const handleBatchMove = (targetStatus: TaskStatus) => {
    checkedItems.forEach((id: string) => moveTask(id, targetStatus, GROCERY_BOARD));
    setCheckedItems(new Set());
    setActiveTab(targetStatus);
  };

  return (
    <div className="relative min-h-screen animate-in fade-in duration-500">
      <div className={`max-w-6xl mx-auto space-y-6 p-4 pb-32 transition-all duration-300 ${isAdminOpen ? 'pr-[400px] opacity-50 lg:opacity-100' : ''}`}>
        
        {/* --- SYSTEM HEADER --- */}
        <div className="flex flex-col md:flex-row justify-between md:items-center px-2 gap-4">
           <div>
             <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tighter uppercase italic leading-none">
               {t('groceries.title', 'Inventory')}
             </h1>
             <p className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.3em] mt-2">
               {currentHouseholdId ? 'Hybrid Supply Chain' : 'Personal Partition Mode'}
             </p>
           </div>

           {/* 🛡️ SCOPE SELECTOR */}
           {currentHouseholdId && (
            <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-inner self-start">
              {[
                { id: 'PERSONAL', icon: Shield, color: 'text-emerald-500' },
                { id: 'BOTH', icon: Layers, color: 'text-indigo-500' },
                { id: 'SHARED', icon: Users, color: 'text-indigo-600' }
              ].map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActiveScope(s.id as DataScope)}
                  className={`px-4 py-2 rounded-xl transition-all flex items-center gap-2 ${activeScope === s.id ? 'bg-white dark:bg-slate-700 shadow-lg ' + s.color : 'text-slate-400'}`}
                >
                  <s.icon className="w-4 h-4" />
                  <span className="text-[10px] font-black uppercase hidden sm:inline">{s.id}</span>
                </button>
              ))}
            </div>
           )}

           <button 
             onClick={() => setIsAdminOpen(true)}
             className="hidden md:block p-3 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
           >
             <Settings className="w-5 h-5 text-slate-500" />
           </button>
        </div>

        {/* --- NAVIGATION TABS --- */}
        <div className="flex bg-slate-100 dark:bg-slate-800 p-1.5 rounded-[1.5rem] gap-1 sticky top-0 z-20 shadow-sm backdrop-blur-md">
          {( [
            { id: 'todo', label: t('groceries.shoppingList', 'Needed'), icon: ShoppingCart },
            { id: 'pantry', label: t('groceries.pantry', 'Stock'), icon: Utensils },
            { id: 'history', label: t('common.history', 'Ledger'), icon: History }
          ] as const).map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setCheckedItems(new Set()); }}
              className={`flex-1 px-3 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${activeTab === tab.id ? 'bg-white dark:bg-slate-700 shadow-sm text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* --- SMART SUGGESTIONS --- */}
        {activeTab === 'todo' && suggestions.length > 0 && (
          <section className="bg-slate-900 dark:bg-indigo-950 rounded-[2.5rem] p-8 text-white shadow-2xl animate-in slide-in-from-top-4">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-4 h-4 text-indigo-400 animate-pulse" />
              <h2 className="text-[10px] font-black uppercase tracking-[0.2em] opacity-70">
                {t('ai.suggestions', 'Cognitive Restock Protocol')}
              </h2>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 no-scrollbar">
              {suggestions.map((s: KanbanTask) => (
                <div key={s.id} className="bg-white/5 backdrop-blur-md p-5 rounded-3xl min-w-[180px] border border-white/10 group hover:bg-white/10 transition-all">
                  <div className="flex justify-between items-start mb-4">
                    <p className="font-black text-sm truncate uppercase italic">{s.title}</p>
                    {s.scope === 'personal' && <Lock className="w-3 h-3 text-emerald-400" />}
                  </div>
                  <button 
                    onClick={() => moveTask(s.id, 'todo', GROCERY_BOARD)}
                    className="w-full py-3 bg-white text-slate-900 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-indigo-50 transition-colors"
                  >
                    {t('common.add', 'Restore')}
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* --- FILTER BAR --- */}
        <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
          {categories.map((cat: string) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-5 py-2.5 rounded-full text-[9px] font-black uppercase tracking-widest whitespace-nowrap border transition-all ${selectedCategory === cat ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-400'}`}
            >
              {cat === 'ALL' ? t('common.all_items', 'Global View') : cat}
            </button>
          ))}
        </div>

        {/* --- GROUPED CONTENT --- */}
        <div className="space-y-10">
          {Object.entries(groupedData).map(([groupName, items]) => (
            <div key={groupName} className="space-y-4">
              <div className="flex items-center gap-2 px-4">
                <ChevronRight className="w-3 h-3 text-indigo-500" />
                <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-[0.3em]">{groupName}</h3>
              </div>

              {(items as KanbanTask[]).map((item: KanbanTask) => (
                <div key={item.id} className={`bg-white dark:bg-slate-900 rounded-[2rem] p-5 border shadow-sm hover:shadow-md transition-all group ${item.scope === 'personal' ? 'border-emerald-100 dark:border-emerald-900/30' : 'border-slate-100 dark:border-slate-800'}`}>
                  <div className="flex flex-col gap-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-5">
                        <button 
                          onClick={() => {
                            const next = new Set(checkedItems);
                            next.has(item.id) ? next.delete(item.id) : next.add(item.id);
                            setCheckedItems(next);
                          }}
                          className={`w-8 h-8 rounded-xl border-2 flex items-center justify-center transition-all ${checkedItems.has(item.id) ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg' : 'border-slate-200 dark:border-slate-800 group-hover:border-indigo-300'}`}
                        >
                          {checkedItems.has(item.id) && <Check className="w-5 h-5" />}
                        </button>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className={`font-black text-xl italic uppercase tracking-tighter transition-all ${checkedItems.has(item.id) ? 'line-through text-slate-300' : 'text-slate-900 dark:text-white'}`}>
                              {item.title}
                            </span>
                            {item.scope === 'personal' && <Lock className="w-3 h-3 text-emerald-500 opacity-50" />}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="w-20 h-2 bg-slate-50 dark:bg-slate-800 rounded-full overflow-hidden hidden sm:block">
                          <div 
                            className={`h-full transition-all duration-1000 ${(item.consumption?.stockLevel || 0) < 0.3 ? 'bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.4)]' : 'bg-indigo-500'}`}
                            style={{ width: `${Math.min(100, (item.consumption?.stockLevel || 0) * 100)}%` }}
                          />
                        </div>
                        <span className="text-[10px] font-black text-slate-400 uppercase tabular-nums">
                          {Math.round((item.consumption?.stockLevel || 0) * 100)}%
                        </span>
                      </div>
                    </div>

                    {activeTab === 'pantry' && item.consumption && (
                      <div className="flex items-center justify-between pt-4 border-t border-slate-50 dark:border-slate-800/50">
                         <div className="flex items-center gap-1 bg-slate-50 dark:bg-slate-800 p-1.5 rounded-2xl">
                            <button 
                              onClick={() => consumeQuantity(item.id, 0.25)}
                              className="px-4 py-2 text-[9px] font-black text-rose-500 hover:bg-white dark:hover:bg-slate-700 rounded-xl transition-all uppercase tracking-widest"
                            >
                              -0.25 {item.consumption.unit}
                            </button>
                            <div className="h-4 w-px bg-slate-200 dark:bg-slate-700 mx-1" />
                            <button 
                              onClick={() => consumeQuantity(item.id, 1)}
                              className="px-4 py-2 text-[9px] font-black text-rose-500 hover:bg-white dark:hover:bg-slate-700 rounded-xl transition-all uppercase tracking-widest"
                            >
                              -1 {item.consumption.unit}
                            </button>
                         </div>
                         <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                           {item.consumption.remainingQuantity} / {item.consumption.quantity} {item.consumption.unit}
                         </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}

          <div className="pt-6">
            <InlineAddGrocery 
            onAdd={handleAddItem} 
            historyTasks={tasks} 
            currentHouseholdId={currentHouseholdId}
            activeDataScope={activeScope}
          />
          </div>
        </div>

        {/* --- ACTION BAR --- */}
        {checkedItems.size > 0 && (
          <div className="fixed bottom-10 left-1/2 -translate-x-1/2 w-full max-w-sm px-6 animate-in slide-in-from-bottom-10 z-50">
            <Button 
              onClick={() => handleBatchMove(activeTab === 'todo' ? 'pantry' : 'history')}
              className={`w-full h-16 shadow-2xl rounded-[2rem] text-xs font-black gap-3 text-white uppercase tracking-[0.2em] transition-all transform active:scale-95 ${activeTab === 'todo' ? 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-500/40' : 'bg-rose-600 hover:bg-rose-700 shadow-rose-500/40'}`}
            >
              {activeTab === 'todo' ? <CheckCircle2 className="w-5 h-5" /> : <Pizza className="w-5 h-5" />}
              {activeTab === 'todo' ? t('groceries.complete_shop', 'Sync to Pantry') : t('groceries.log_consumption', 'Mark Expended')} ({checkedItems.size})
            </Button>
          </div>
        )}
      </div>

      {/* --- ADMIN DRAWER --- */}
      {isAdminOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          <div 
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300" 
            onClick={() => setIsAdminOpen(false)} 
          />
          <CategoryManager 
            onClose={() => setIsAdminOpen(false)}
            onSave={(newMap: Record<string, string[]>) => {
              console.log("Categorization engine updated:", newMap);
              setIsAdminOpen(false);
            }}
            currentHouseholdId={currentHouseholdId}
            isAdmin={true}
          />
        </div>
      )}
    </div>
  );
};